/**
 * Pure helpers that collapse the N per-pair `pair_matchup_count` rows
 * into a single synthetic "Number of Matchups per Team" summary row for
 * the constraints table.
 *
 * Why: the data model writes one constraint per team-pair (the solver
 * needs that granularity), but for the constraints table that's
 * unwieldy at scale — 50 teams = 1225 rows. The matrix modal is the
 * intended editor for this constraint type, so the table only needs
 * to surface a summary that points users at the modal.
 *
 * Pure module on purpose so the collapse logic is unit-testable
 * without dragging React/Emotion through Jest.
 */

import type { SchedulerConstraint } from '../hooks/useSchedulerConstraints'

export const PAIR_MATCHUP_COUNT_CODE = 'pair_matchup_count'

/** Synthetic row id — keep stable so the table can key off it. */
export const COLLAPSED_PAIR_MATCHUP_ID = '__collapsed_pair_matchup__'

/**
 * Marker shape grafted onto the SchedulerConstraint-like row so the
 * column renderers can branch on it. Carries the underlying ids so
 * action handlers can fan out (bulk delete, bulk toggle).
 */
export interface CollapsedPairMatchupRow {
  /** Discriminator. */
  _isCollapsed: true
  /** Stable synthetic id. */
  id: string
  /** Constraint-shape fields the table renderers expect. */
  type: {
    id: string
    code: string
    name: string
    category: string
  }
  is_hard: boolean
  is_enabled: boolean
  description: string | null
  division_uids: string[] | null
  team_uids: string[] | null
  sort_order: number
  parameters: Record<string, unknown>
  /** Underlying constraint ids the synthetic row represents. */
  _collapsedIds: string[]
  /** Pre-computed display strings so the column renderers stay simple. */
  _summary: {
    pairCount: number
    countSummary: string
    scopeSummary: string
    isHardMajorityHard: boolean
    enabledCount: number
    disabledCount: number
  }
}

/**
 * Build the summary fields for the collapsed row. Pulled out so tests
 * can pin the description copy without rendering anything.
 */
export function summarizePairMatchupConstraints(
  constraints: SchedulerConstraint[]
): CollapsedPairMatchupRow['_summary'] {
  const pairCount = constraints.length

  // Most-common count — group by parameters.count.
  const countFreq: Record<number, number> = {}
  for (const c of constraints) {
    const n = c.parameters.count
    if (typeof n === 'number') {
      countFreq[n] = (countFreq[n] ?? 0) + 1
    }
  }
  const distinctCounts = Object.keys(countFreq).length
  let topCount: number | null = null
  let topCountHits = 0
  for (const [k, v] of Object.entries(countFreq)) {
    if (v > topCountHits) {
      topCountHits = v
      topCount = Number(k)
    }
  }
  const countSummary =
    distinctCounts === 0
      ? 'no counts set'
      : distinctCounts === 1
      ? `${topCount} games each`
      : `counts vary (mostly ${topCount})`

  // Scope: tally rows with division_uids null/[] vs. scoped.
  let globalCount = 0
  let scopedCount = 0
  for (const c of constraints) {
    if (c.division_uids === null || c.division_uids.length === 0) {
      globalCount += 1
    } else {
      scopedCount += 1
    }
  }
  const scopeSummary =
    globalCount > 0 && scopedCount > 0
      ? 'mixed scope'
      : globalCount > 0
      ? 'league-wide'
      : 'per-division'

  // is_hard majority — used for the HARD/SOFT badge on the summary row.
  let hardCount = 0
  for (const c of constraints) {
    if (c.is_hard) {
      hardCount += 1
    }
  }
  const isHardMajorityHard = hardCount * 2 >= pairCount

  let enabledCount = 0
  for (const c of constraints) {
    if (c.is_enabled) {
      enabledCount += 1
    }
  }

  return {
    pairCount,
    countSummary,
    scopeSummary,
    isHardMajorityHard,
    enabledCount,
    disabledCount: pairCount - enabledCount,
  }
}

/**
 * Build the synthetic CollapsedPairMatchupRow from the underlying
 * pair_matchup_count constraints. Returns null when the input is empty
 * (caller should not insert a row in that case).
 */
export function buildCollapsedPairMatchupRow(
  constraints: SchedulerConstraint[]
): CollapsedPairMatchupRow | null {
  if (constraints.length === 0) {
    return null
  }

  const summary = summarizePairMatchupConstraints(constraints)

  // Union of division_uids across all rows (null means "applies to all"
  // and dominates — if any row is global, the summary reads as global).
  const divUidSet = new Set<string>()
  let anyGlobal = false
  for (const c of constraints) {
    if (c.division_uids === null || c.division_uids.length === 0) {
      anyGlobal = true
    } else {
      for (const u of c.division_uids) {
        divUidSet.add(u)
      }
    }
  }
  const divisionUids = anyGlobal ? null : Array.from(divUidSet)

  // Union of unique team_uids across all rows (each row has 2 teams).
  const teamUidSet = new Set<string>()
  for (const c of constraints) {
    for (const u of c.team_uids ?? []) {
      teamUidSet.add(u)
    }
  }

  // Borrow the first constraint's `type` block — code/name/category
  // are identical across all pair_matchup_count rows.
  const proto = constraints[0]

  return {
    _isCollapsed: true as const,
    id: COLLAPSED_PAIR_MATCHUP_ID,
    type: {
      id: proto.type.id,
      code: proto.type.code,
      name: proto.type.name,
      category: proto.type.category,
    },
    is_hard: summary.isHardMajorityHard,
    // The summary row reads as "enabled" if any underlying row is enabled —
    // matches the toggle's intent (clicking off bulk-disables all).
    is_enabled: summary.enabledCount > 0,
    description: null,
    division_uids: divisionUids,
    team_uids: Array.from(teamUidSet),
    sort_order: Math.min(...constraints.map((c) => c.sort_order)) || 0,
    parameters: {},
    _collapsedIds: constraints.map((c) => c.id),
    _summary: summary,
  }
}
